<template>
    <AutomationEmptyStateDisconnectedToProvider
        v-if="isProviderDown"
        class="automation-listing__empty-state"
    />
    <div v-else class="automation-listing">
        <AutomationListingEmptyStateNoAutomation
            v-if="allAutomations.length === 0 && !isLoadingTable"
            class="automation-listing__empty-state"
        />
        <template v-else>
            <ListingSettingsHeader
                :columns="columns"
                :enableColumnsSettings="false"
                searchable
                :searchValue="searchValue"
                @search="searchAttributes"
            />
            <TableLoading v-if="isLoadingTable" />
            <CompassTable
                v-else
                size="large"
                :border="true"
                :isCheckable="false"
                class="automation-listing__table"
            >
                <AutomationListingTableHead :columns="columns" @sort-table-head="handleSort" />
                <td
                    v-if="!automations.length"
                    colspan="100%"
                    class="automation-listing__empty-state__no-result"
                >
                    <AutomationListingEmptyStateNoResult @reset-filters="resetFilters" />
                </td>
                <CompassTableBody v-else>
                    <AutomationListingTableRow
                        v-for="(automation, index) in automations"
                        :key="index"
                        :automation="automation"
                        :columns="columns"
                        @update-automation="updateAutomation"
                    />
                </CompassTableBody>
            </CompassTable>
            <TablePagination
                v-if="pagination.total > 0"
                :pagination="pagination"
                :isLoading="isLoadingTable"
                @update-pagination="updatePagination"
            />
        </template>
    </div>
</template>

<script>
import AutomationEmptyStateDisconnectedToProvider from 'vcomponents/automation/AutomationEmptyStateDisconnectedToProvider.vue';
import AutomationListingEmptyStateNoAutomation from 'vcomponents/automation/listing/emptyState/AutomationListingEmptyStateNoAutomation.vue';
import AutomationListingEmptyStateNoResult from 'vcomponents/automation/listing/emptyState/AutomationListingEmptyStateNoResult.vue';
import AutomationListingTableHead from 'vcomponents/automation/listing/table/AutomationListingTableHead.vue';
import AutomationListingTableRow from 'vcomponents/automation/listing/table/AutomationListingTableRow.vue';
import ListingSettingsHeader from 'vcomponents/common/listing/header/ListingSettingsHeader.vue';
import TableLoading from 'vcomponents/common/table/TableLoading.vue';
import TablePagination from 'vcomponents/common/table/TablePagination.vue';

import { getAutomations } from 'api/automation/automationService.js';
import { createNamespacedHelpers } from 'vuex';
const { mapState: mapStateSystem } = createNamespacedHelpers('system');

export default {
    components: {
        AutomationEmptyStateDisconnectedToProvider,
        AutomationListingEmptyStateNoAutomation,
        AutomationListingEmptyStateNoResult,
        AutomationListingTableHead,
        AutomationListingTableRow,
        ListingSettingsHeader,
        TableLoading,
        TablePagination,
    },
    data() {
        return {
            allAutomations: [],
            automations: [],
            columns: [
                {
                    order: 0,
                    label: this.$t.__('common.labels.name'),
                    code: 'name',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    sortable: { direction: '', orderBy: 'name' },
                    maxWidth: '424px',
                },
                {
                    order: 1,
                    label: this.$t.__('common.labels.last_modification'),
                    code: 'date',
                    fixed: 'right',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    sortable: { direction: 'desc', orderBy: 'date' },
                    maxWidth: '200px',
                    minWidth: '200px',
                },
                {
                    order: 2,
                    label: this.$t.__('common.labels.status'),
                    code: 'status',
                    fixed: 'right',
                    alignContent: 'center',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    sortable: { direction: '', orderBy: 'status' },
                    maxWidth: '64px',
                    minWidth: '64px',
                },
                {
                    order: 3,
                    label: '',
                    code: 'actions',
                    fixed: 'right',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    maxWidth: '64px',
                    minWidth: '64px',
                },
            ],
            isLoadingTable: true,
            searchValue: '',
            pagination: {
                limit: 25,
                offset: 0,
                total: 0,
                currentPage: 1,
            },
            sort: [],
            isProviderDown: false,
        };
    },
    computed: {
        ...mapStateSystem(['errorsHandler']),
    },
    created() {
        EventBus.$on('automation-deleted', () => {
            this.getAutomations();
        });
        this.initializeSort();
        this.getAutomations();
    },
    methods: {
        getAutomations() {
            this.isLoadingTable = true;
            getAutomations()
                .then((response) => {
                    this.allAutomations = response.data.data.map((flow) => ({
                        id: flow.id,
                        name: flow.version.displayName,
                        date: flow.updated || '-',
                        status: flow.status === 'ENABLED',
                        actions: true,
                        version: flow.version,
                    }));
                    this.updateAutomations();
                })
                .catch((error) => {
                    this.allAutomations = [];
                    this.isProviderDown = true;
                    this.errorsHandler.logException(error);
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: this.$t.__('common.labels.toast.error'),
                    });
                })
                .finally(() => {
                    this.isLoadingTable = false;
                });
        },
        searchAttributes(value) {
            this.searchValue = value;
            this.updateAutomations();
        },
        updatePagination(newPagination) {
            this.pagination = {
                ...newPagination,
                offset: (newPagination.currentPage - 1) * newPagination.limit,
            };
            this.updateAutomations();
        },
        initializeSort() {
            this.sort = this.columns
                .filter((col) => col.sortable && col.sortable.orderBy)
                .map((col) => ({
                    orderBy: col.sortable.orderBy,
                    direction: col.sortable.direction,
                }));
        },
        updateAutomations() {
            if (!this.allAutomations) {
                return;
            }

            let filteredAutomations = [...this.allAutomations];

            // Filter search value
            if (this.searchValue) {
                filteredAutomations = filteredAutomations.filter((automation) =>
                    automation.name.toLowerCase().includes(this.searchValue.toLowerCase()),
                );
            }

            // Sort columns
            for (const sort of this.sort) {
                if (sort.direction) {
                    filteredAutomations.sort((a, b) => {
                        let comparison = 0;
                        const orderBy = sort.orderBy;

                        if (orderBy === 'date') {
                            const aValue = new Date(a.date).getTime() || 0;
                            const bValue = new Date(b.date).getTime() || 0;
                            comparison = aValue - bValue;
                        } else if (orderBy === 'name') {
                            comparison = a.name.localeCompare(b.name, undefined, {
                                numeric: true,
                                sensitivity: 'base',
                            });
                        } else if (orderBy === 'status') {
                            const normalizeStatus = (status) => {
                                if (status === true) return 1;
                                if (status === false) return 0;
                                return -1;
                            };
                            const aValue = normalizeStatus(a.status);
                            const bValue = normalizeStatus(b.status);
                            comparison = aValue - bValue;
                        } else {
                            comparison = a[orderBy] - b[orderBy];
                        }

                        return sort.direction === 'asc' ? comparison : -comparison;
                    });
                }
            }

            // Pagination
            this.pagination.total = filteredAutomations.length;
            this.pagination.offset =
                this.pagination.currentPage === 1
                    ? 0
                    : (this.pagination.currentPage - 1) * this.pagination.limit;
            const start = this.pagination.offset;
            const end = start + this.pagination.limit;
            this.automations = filteredAutomations.slice(start, end);
        },
        updateAutomation({ id, status }) {
            const automation = this.allAutomations.find((automation) => automation.id === id);
            if (automation) {
                automation.status = status;
            }
            this.updateAutomations();
        },
        handleSort(sortParams) {
            const orderTransitions = {
                asc: 'desc',
                desc: '',
                '': 'asc',
            };
            const existingSort = this.sort.find((s) => s.orderBy === sortParams.orderBy);
            if (existingSort) {
                existingSort.direction = orderTransitions[existingSort.direction];
                if (!existingSort.direction) {
                    this.sort = this.sort.filter((s) => s.orderBy !== sortParams.orderBy);
                }
            } else {
                this.sort.push({ orderBy: sortParams.orderBy, direction: 'asc' });
            }
            this.updateAutomations();
        },
        resetFilters() {
            this.searchValue = '';
            this.initializeSort();
            this.updateAutomations();
        },
    },
};
</script>

<style lang="scss" scoped>
.automation-listing {
    max-height: calc(100% - 56px);
    display: flex;
    flex-direction: column;
    background: var(--colors-common-background-default);
    padding: 0 var(--spacing-3);
    border-radius: var(--spacing-0);

    &::v-deep {
        .compass-tooltip,
        .compass-tooltip__element {
            max-width: inherit;
        }
    }

    &__empty-state {
        height: 560px;
        display: flex;
        justify-content: center;
        background-color: var(--colors-common-background-default);

        &__no-result {
            padding: 112px;
        }
    }
}
</style>
